import React, { useEffect, useState } from "react";
import { useLocation } from "@reach/router";
import { graphql, useStaticQuery } from "gatsby";
import { Link, useI18next } from "gatsby-plugin-react-i18next";
import classNames from "classnames";

import Container from "./Container";

import logo from "../assets/new/Hero_Desktop_Gmining_logo_GoldWhite_WhiteText.svg";
import burgerMenuIcon from "../assets/header/burger-menu.svg";
import closeMenuIcon from "../assets/header/close-menu.svg";
import YouTubeLogo from "../assets/footer/youtube.svg";
import TwitterLogo from "../assets/footer/twitter.svg";
import LinkedInLogo from "../assets/footer/linkedin.svg";
import FacebookLogo from "../assets/footer/facebook.svg";
import TsxLogo from "../assets/new/Nav_Mobile_TSX_Logo_White.svg";
import OtcLogo from "../assets/new/Nav_Mobile_OTC_Logo_White.svg";
import CmeLogo from "../assets/new/Nav_Mobile_CME_Logo_white.svg";
import NavGreenLogo from "../assets/new/Nav_GreenLogoBG.svg";

import { commaDelineation } from "../utils/commaDelineation";
import Button from "./basic/Button";
import { StaticImage } from "gatsby-plugin-image";

const languageNames = {
  "en-US": "EN",
  fr: "FR",
};

const HeaderSubMenuItem = ({ item }) => {
  const [isHoverOpen, setIsHoverOpen] = useState(false);
  const location = useLocation();

  const hasSubItems = Boolean(item?.subItems?.filter((subItem) => !subItem?.hideOnHeader)?.length);
  const isActive = location.pathname.includes(item.url);

  return (
    <div
      className={classNames(
        "relative mx-15px cursor-pointer",
        "after:content-[''] after:h-1 after:w-full after:bg-gold after:absolute after:left-0 after:transition-all after:duration-[250] after:ease-in-out",
        {
          "after:opacity-0 after:bottom-1": !isHoverOpen && !isActive,
          "after:opacity-100 after:-bottom-1px": isHoverOpen || isActive,
        },
      )}
      onMouseEnter={() => setIsHoverOpen(true)}
      onMouseLeave={() => setIsHoverOpen(false)}
    >
      <Link
        to={item.url}
        className={classNames("flex items-center h-50px md:h-90px navlinks", {
          "text-gold": isActive,
          "text-white": !isActive,
        })}
      >
        {item.label}
      </Link>
      {isHoverOpen && hasSubItems && (
        <div className="absolute z-30 top-50px md:top-90px -left-4 flex flex-col bg-white">
          {item.subItems.map((subItem, index) => (
            <Link
              key={index}
              to={subItem.url}
              className={classNames("py-4 pl-4 pr-8 whitespace-nowrap bg-[#f6f9f9] hover:bg-[#edefef] sublink", {
                "text-gold": location.pathname.endsWith(`${subItem.url}/`),
              })}
            >
              {subItem.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const MobileHeaderSubMenuItem = ({ item, onClick, activeLink }) => {
  const location = useLocation();

  const isActive = !activeLink ? location.pathname.includes(item.url) : item?.url === activeLink;

  return (
    <>
      {item?.subItems?.length ? (
        <div className="flex flex-col cursor-pointer leading-[140%]" onClick={() => onClick(item)}>
          <div className="flex items-center py-10px">
            <div
              className={classNames("navlinks", {
                "text-gold": isActive,
              })}
            >
              {item.label}
            </div>
          </div>
        </div>
      ) : (
        <Link
          to={item.url}
          className={classNames("pb-12px py-10px px-48px md:px-96px navlinks", {
            "text-gold": isActive,
          })}
        >
          {item.label}
        </Link>
      )}
    </>
  );
};

const MobileHeaderSubItems = ({ activeLink, headerData }) => {
  const location = useLocation();
  const links = headerData?.links.find((item) => item.url === activeLink);

  return (
    <div className="flex flex-col overflow-x-hidden overflow-y-scroll">
      {links?.subItems.map((item, index) => (
        <Link
          to={item.url}
          key={index}
          className={classNames("py-10px last:mb-0", {
            "text-gold": `${item.url}/` === location.pathname,
          })}
        >
          <span className="small-sublink">{item.label}</span>
        </Link>
      ))}
    </div>
  );
};

const LanguagePicker = () => {
  const { language, languages, changeLanguage } = useI18next();
  const [isHoverOpen, setIsHoverOpen] = useState(false);

  return (
    <div
      className={classNames(
        "relative flex items-center justify-center w-24px h-50px md:h-90px ml-45px mr-15px cursor-pointer",
        "before:content-[''] before:absolute before:-left-30px before:h-28px before:w-2px before:bg-white",
        "after:content-[''] after:h-4px after:w-full after:bg-gold after:absolute after:left-0 after:transition-all after:duration-[250] after:ease-in-out",
        {
          "after:opacity-0 after:bottom-1": !isHoverOpen,
          "after:opacity-100 after:-bottom-1px": isHoverOpen,
        },
      )}
      onMouseEnter={() => setIsHoverOpen(true)}
      onMouseLeave={() => setIsHoverOpen(false)}
    >
      <div className="relative text-gold navlinks">{languageNames?.[language]}</div>
      {isHoverOpen && (
        <div className="absolute z-30 top-50px md:top-90px -left-4 flex flex-col bg-white">
          {languages.map((lang, index) => (
            <button
              key={index}
              onClick={() => changeLanguage(lang)}
              className={classNames("py-16px pl-16px pr-32px whitespace-nowrap", {
                "text-gold": language === lang,
              })}
            >
              {languageNames?.[lang]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const LanguagePickerMobile = () => {
  const { language, languages, changeLanguage } = useI18next();
  const [lang, setLang] = useState(language);
  const onChangeLanguage = (e) => {
    e.stopPropagation();
    setLang((prevLang) => {
      const lang = prevLang === languages[0] ? languages[1] : languages[0];
      changeLanguage(lang);
      return lang;
    });
  };

  return (
    <div className="w-full mt-24px">
      <Button
        variant="transparent"
        className="border uppercase !text-gold !font-normal text-20px w-full"
        onClick={(e) => onChangeLanguage(e)}
      >
        {languageNames?.[lang]}
      </Button>
    </div>
  );
};

const SocialLink = ({ link, src }) => {
  return (
    <a href={link} className="w-fit mr-36px last:mr-0">
      <img src={src} alt="Social Link" className="w-24px h-24px" />
    </a>
  );
};

const HeaderSocialLinks = () => {
  return (
    <div className="flex justify-center items-center">
      <SocialLink link="/" src={LinkedInLogo} />
      <SocialLink link="/" src={TwitterLogo} />
      <SocialLink link="/" src={YouTubeLogo} />
      <SocialLink link="/" src={FacebookLogo} />
    </div>
  );
};

const StockInfoItem = ({ price, logo }) => {
  return (
    <div className={classNames("relative flex flex-col items-center justify-between text-white font-[300]")}>
      <img src={logo} alt={"Stock Logo"} className="w-72px h-32px" />
      <div className="mt-8px text-18px leading-[140%] font-lato">${price}</div>
    </div>
  );
};

const StockInfo = ({ stockItem }) => {
  return (
    <>
      {stockItem && (
        <div className="mt-26px flex items-center justify-between">
          <StockInfoItem price={stockItem.strockGminPrice} logo={TsxLogo} />
          <StockInfoItem price={stockItem.strockOtcPrice} logo={OtcLogo} />
          <StockInfoItem price={stockItem.spotGold} logo={CmeLogo} />
        </div>
      )}
    </>
  );
};

const isBrowser = typeof window !== "undefined";

const Header = () => {
  const { language } = useI18next();
  const query = useStaticQuery(graphql`
    query HeaderQuery {
      data: allContentfulHeaderAndFooter {
        nodes {
          links {
            label
            url
            subItems {
              label
              url
              hideOnHeader
            }
          }
          node_locale
        }
      }
    }
  `);

  useEffect(() => {
    if (isBrowser) {
      document.documentElement.lang = language;
    }
  }, [language]);

  const [stockItem, setStockItem] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeLink, setActiveLink] = useState("");

  const headerData = query?.data?.nodes.filter((data) => data.node_locale === language)[0];

  const handler = (navLink) => {
    setActiveLink((prevValue) => (prevValue === navLink?.url ? "" : navLink?.url));
  };

  useEffect(() => {
    async function fetchData() {
      try {
        var stock = await fetch(
          `${process.env.GATSBY_STOCK_TRACK_URL}&symbol=${process.env.GATSBY_STOCK_SYMBOL}&apikey=${process.env.GATSBY_STOCK_API_KEY}`,
          {
            headers: { "Content-Type": "application/json" },
          },
        )
          .then((re) => re.json())
          .then((res) => res["Global Quote"]);
      } catch (error) {
        console.log("Stock fetch error: ", error);
      }

      try {
        var stockOtc = await fetch(
          `${process.env.GATSBY_STOCK_TRACK_URL}&symbol=${process.env.GATSBY_STOCK_OTC_SYMBOL}&apikey=${process.env.GATSBY_STOCK_API_KEY}`,
          {
            headers: { "Content-Type": "application/json" },
          },
        )
          .then((re) => re.json())
          .then((res) => res["Global Quote"]);
      } catch (error) {
        console.log("Stock OTC fetch error: ", error);
      }

      try {
        var spot = await fetch(process.env.GATSBY_GOLD_API_URL, {
          headers: {
            "Content-Type": "application/json",
            "x-access-token": process.env.GATSBY_GOLD_API_KEY,
          },
        })
          .then((re) => re.json())
          .then((res) => res["ask"]);
      } catch (error) {
        console.log("Spot fetch error: ", error);
      }

      setStockItem({
        strockGminPrice: stock ? `${Number(stock["05. price"]).toFixed(2)} CAD` : "0",
        strockOtcPrice: stockOtc ? `${Number(stockOtc["05. price"]).toFixed(2)} USD` : "0",
        spotGold: `${commaDelineation(Number(spot).toFixed(2))} USD`,
      });
    }
    fetchData();
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [isMenuOpen]);

  return (
    <header className="fixed top-0 z-40 w-full">
      <Container
        outerContainerClassName={classNames("bg-dark-blue !overflow-visible !py-0", {
          "border-b-[1px] border-white": isMenuOpen,
        })}
        innerContainerClassName="!px-16px md:!px-36px !max-w-full"
      >
        <div className="flex items-center h-50px md:h-90px uppercase">
          <Link to="/" className="shrink-0">
            <img src={logo} alt="Logo" className="h-32px md:h-60px" />
          </Link>
          <nav className="hidden md:flex items-center ml-auto">
            {headerData?.links?.map((navLink, index) => (
              <React.Fragment key={index}>
                <HeaderSubMenuItem item={navLink} />
              </React.Fragment>
            ))}
            <LanguagePicker />
            <div
              className={classNames(
                "h-60px ml-45px mr-13px hidden md:block relative",
                "before:content-[''] before:absolute before:-left-30px before:top-1/2 before:-translate-y-1/2 before:h-28px before:w-2px before:bg-white",
              )}
            >
              <StaticImage
                src="../assets/header/venture50.svg"
                alt="Venture 50"
                layout="constrained"
                className="h-60px w-42px object-cover"
              />
            </div>
          </nav>
          <button
            className="md:hidden ml-auto w-24px h-16px flex items-center justify-center"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? (
              <img src={closeMenuIcon} alt="Menu Close icon" className="w-full h-full p-1 md:p-1.5" />
            ) : (
              <img src={burgerMenuIcon} alt="Menu icon" className="w-full h-full" />
            )}
          </button>
        </div>
      </Container>
      {isMenuOpen && (
        <div className="grid grid-flow-row auto-rows-[minmax(6.25rem,1fr)25rem] md:hidden absolute top-0 left-0 right-0 ove bottom-0 h-[calc(100svh+5.6rem)] mt-51px px-16px overflow-y-scroll bg-dark-blue">
          <div className="relative top-0 grid grid-cols-[1fr_1fr_1fr] w-full px-10px mt-18px bg-dark-blue text-white uppercase">
            <div className="relative z-10 flex flex-col overflow-x-hidden">
              {headerData?.links?.map((navLink, index) => (
                <React.Fragment key={index}>
                  <MobileHeaderSubMenuItem item={navLink} onClick={handler} activeLink={activeLink} />
                </React.Fragment>
              ))}
            </div>
            <div
              className={classNames("relative flex justify-center py-18px", {
                hidden: !activeLink,
              })}
            >
              <div className="w-1px h-full bg-cream-white" />
            </div>
            {!activeLink ? (
              <img src={NavGreenLogo} className="absolute -top-32px right-0 w-396px" alt="nav-green-logo" />
            ) : (
              <MobileHeaderSubItems headerData={headerData} activeLink={activeLink} />
            )}
          </div>
          <div className="bottom-0 w-full px-10px pt-32px pb-160px mt-auto">
            <HeaderSocialLinks />
            <LanguagePickerMobile />
            <StockInfo stockItem={stockItem} />
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
